'use client'

import * as React from 'react'
import { Search, Plus, MoreHorizontal, Shield, UserCheck, UserX, Mail, Building2, Clock } from 'lucide-react'
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth'
import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Separator } from 'components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { cn } from 'lib/utils'
import { toast } from 'sonner'
import { db, getSecondaryAuth } from '../../../../firebase'
import { useUser } from 'components/context/user-context'

const roleBadgeColors = {
  'Vice Chancellor': 'bg-primary/15 text-primary border-primary/30',
  Admin: 'bg-destructive/15 text-destructive border-destructive/30',
  Accountant: 'bg-chart-3/15 text-chart-3 border-chart-3/30',
}

export default function UserManagementPage() {
  const { canEdit } = useUser()
  const [search, setSearch] = React.useState('')
  const [roleFilter, setRoleFilter] = React.useState('all')
  const [users, setUsers] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [availableRoles, setAvailableRoles] = React.useState([])
  const [availableDepartments, setAvailableDepartments] = React.useState([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [fullName, setFullName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [nationalId, setNationalId] = React.useState('')
  const [pfNumber, setPfNumber] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [selectedRole, setSelectedRole] = React.useState('')
  const [selectedDepartment, setSelectedDepartment] = React.useState('')
  const userCollectionName = 'users'
  const roleCollectionName = 'userRoles'
  const departmentCollectionName = 'Departments'

  const loadUsers = React.useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, userCollectionName))
      const records = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }))
      setUsers(records)
    } catch {
      toast.error('Unable to load users from Firestore.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function load() {
      setIsLoading(true)
      await loadUsers()
    }

    if (isMounted) {
      load()
    }

    return () => {
      isMounted = false
    }
  }, [loadUsers])

  React.useEffect(() => {
    let isMounted = true

    async function loadRoles() {
      try {
        const snapshot = await getDocs(collection(db, roleCollectionName))
        if (!isMounted) return
        const roles = snapshot.docs
          .map((doc) => doc.data()?.userRole)
          .filter(Boolean)
        setAvailableRoles(Array.from(new Set(roles)))
      } catch {
        toast.error('Unable to load roles from Firestore.')
      }
    }

    loadRoles()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadDepartments() {
      try {
        const snapshot = await getDocs(collection(db, departmentCollectionName))
        if (!isMounted) return
        const departments = snapshot.docs
          .map((doc) => doc.data()?.schools)
          .filter(Boolean)
        setAvailableDepartments(Array.from(new Set(departments)))
      } catch {
        toast.error('Unable to load departments from Firestore.')
      }
    }

    loadDepartments()

    return () => {
      isMounted = false
    }
  }, [])

  function getUserName(user) {
    return user.fullName || user.name || user.email || 'Unknown'
  }

  function getDepartment(user) {
    return user.department || user.institutionalUnit || user.unit || '—'
  }

  function getUserRole(user) {
    return user.userType || user.role || '—'
  }

  function formatDate(value) {
    if (!value) return '—'
    if (typeof value?.toDate === 'function') {
      return value.toDate().toLocaleDateString('en-KE', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return '—'
    return parsed.toLocaleDateString('en-KE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const roleOptions = React.useMemo(() => {
    const roles = new Set()
    users.forEach((user) => {
      const role = getUserRole(user)
      if (role && role !== '—') {
        roles.add(role)
      }
    })
    return Array.from(roles)
  }, [users])

  const filteredUsers = users.filter((user) => {
    const nameValue = getUserName(user).toLowerCase()
    const emailValue = (user.email || '').toLowerCase()
    const matchesSearch =
      nameValue.includes(search.toLowerCase()) ||
      emailValue.includes(search.toLowerCase())
    const matchesRole =
      roleFilter === 'all' || getUserRole(user) === roleFilter
    return matchesSearch && matchesRole
  })

  function getCreateUserErrorMessage(error) {
    const code = error?.code || ''
    if (code === 'auth/email-already-in-use') return 'Email is already in use.'
    if (code === 'auth/invalid-email') return 'Please enter a valid email.'
    if (code === 'auth/weak-password') return 'Password is too weak.'
    return 'Unable to create user. Please try again.'
  }

  function resetForm() {
    setFullName('')
    setEmail('')
    setNationalId('')
    setPfNumber('')
    setPassword('')
    setSelectedRole('')
    setSelectedDepartment('')
  }

  async function handleCreateUser(event) {
    event.preventDefault()
    if (!fullName || !email || !nationalId || !password || !selectedRole || !selectedDepartment) {
      toast.error('Please fill all required fields.')
      return
    }

    setIsSubmitting(true)
    const secondaryAuth = getSecondaryAuth()
    let createdUser = null

    try {
      const credential = await createUserWithEmailAndPassword(
        secondaryAuth,
        email.trim(),
        password,
      )
      createdUser = credential.user
      await setDoc(doc(db, userCollectionName, createdUser.uid), {
        email: email.trim(),
        fullName: fullName.trim(),
        userId: createdUser.uid,
        userType: selectedRole,
        department: selectedDepartment,
        nationalId: nationalId.trim(),
        pfNumber: pfNumber.trim() || '',
        joinedAt: serverTimestamp(),
        isActive: true,
      })
      toast.success('User created successfully')
      resetForm()
      await loadUsers()
    } catch (error) {
      if (createdUser) {
        toast.error('User created in Auth, but profile failed to save. Please try again.')
      } else {
        toast.error(getCreateUserErrorMessage(error))
      }
    } finally {
      try {
        await signOut(secondaryAuth)
      } catch {
        // ignore secondary sign-out failures
      }
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            User Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage system users, roles, and access permissions
          </p>
        </div>
        {canEdit ? (
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-accent gap-2">
                <Plus className="size-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">Add New User</DialogTitle>
              <DialogDescription>
                Create a new user account and assign a role
              </DialogDescription>
            </DialogHeader>
            <form
              className="flex flex-col gap-4 mt-4"
              onSubmit={handleCreateUser}
            >
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Full name</Label>
                <Input
                  placeholder="e.g., Dr. John Omondi"
                  className="h-9"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Email</Label>
                <Input
                  type="email"
                  placeholder="e.g., j.omondi@jooust.ac.ke"
                  className="h-9"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">National ID</Label>
                  <Input
                    placeholder="e.g., 12345678"
                    className="h-9"
                    value={nationalId}
                    onChange={(event) => setNationalId(event.target.value)}
                    required
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">
                    PF number <span className="text-xs text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    placeholder="e.g., PF-10293"
                    className="h-9"
                    value={pfNumber}
                    onChange={(event) => setPfNumber(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Password</Label>
                <Input
                  type="password"
                  placeholder="Set a temporary password"
                  className="h-9"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Role</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {availableRoles.length === 0 ? (
                        <SelectItem value="no-roles" disabled>
                          No roles available
                        </SelectItem>
                      ) : (
                        availableRoles.map((role) => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Department</Label>
                  <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {availableDepartments.length === 0 ? (
                        <SelectItem value="no-departments" disabled>
                          No departments available
                        </SelectItem>
                      ) : (
                        availableDepartments.map((department) => (
                          <SelectItem key={department} value={department}>{department}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                type="submit"
                className="mt-2 bg-primary text-primary-foreground hover:bg-accent"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create User'}
              </Button>
            </form>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="border border-border">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
              <UserCheck className="size-4 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">
                {users.filter((u) => u.isActive !== false).length}
              </p>
              <p className="text-xs text-muted-foreground">Active users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-9 items-center justify-center rounded-lg bg-warning/10">
              <UserX className="size-4 text-warning" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">
                {users.filter((u) => u.isActive === false).length}
              </p>
              <p className="text-xs text-muted-foreground">Inactive users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-9 items-center justify-center rounded-lg bg-destructive/10">
              <Shield className="size-4 text-destructive" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">
                {users.filter((u) => getUserRole(u) === 'Admin').length}
              </p>
              <p className="text-xs text-muted-foreground">Admins</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex size-9 items-center justify-center rounded-lg bg-success/10">
              <Clock className="size-4 text-success" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{users.length}</p>
              <p className="text-xs text-muted-foreground">Total users</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            className="pl-10 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px] h-9">
            <Shield className="mr-2 size-3.5" />
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roleOptions.map((role) => (
              <SelectItem key={role} value={role}>{role}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users List */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : null}
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : null}
              {filteredUsers.map((user) => {
                const name = getUserName(user)
                const role = getUserRole(user)
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-9">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                            {name
                              .split(' ')
                              .map((part) => part[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-foreground">{name}</span>
                          {user.isActive === false ? (
                            <Badge variant="outline" className="w-fit text-[10px] text-muted-foreground">
                              Inactive
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Mail className="size-3" />
                        {user.email || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Building2 className="size-3" />
                        {getDepartment(user)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('text-[10px] border', roleBadgeColors[role] || 'bg-muted text-muted-foreground')}>
                        {role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-[10px] text-muted-foreground">
                        {formatDate(user.joinedAt || user.createdAt || user.lastLogin)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-7">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit ? (
                            <>
                              <DropdownMenuItem>Edit User</DropdownMenuItem>
                              <DropdownMenuItem>Change Role</DropdownMenuItem>
                              <DropdownMenuItem>Assign Projects</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive">
                                {user.isActive === false ? 'Activate' : 'Deactivate'} User
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
